/**
 * People-attribution S10: the React bridge to the ONE «Traffic into this
 * funnel» method. Fetches ONCE per request identity (the serialized request —
 * a unit switch is a different cohort and refetches, S8 lesson: never key the
 * effect on the hook's own state), ignores late results only after a real
 * unmount, and re-arms the mounted flag on mount (StrictMode mounts twice).
 */
import React from 'react';
import type { FunnelInsightsApiClient } from '../api/insightsClient';
import type { FunnelUnit } from '../funnelUnit';
import type { FunnelTrafficSourcesData } from '../types';

export interface FunnelTrafficSourcesQuery {
  projectId: string;
  onboardingId: string;
  versionId: string;
  alias?: string;
  /** Zoned ISO bounds (already through zonedDayBoundIso). */
  from: string;
  to: string;
  tz?: string;
  unit: FunnelUnit;
  attributionModelId?: string;
}

export interface FunnelTrafficSourcesState {
  state: 'idle' | 'loading' | 'ready' | 'failed';
  data?: FunnelTrafficSourcesData;
  error?: string;
  /** Freshness stamp of the served payload (response.asOf). */
  asOf?: string | null;
}

export function useFunnelTrafficSources(
  client: Pick<FunnelInsightsApiClient, 'getReportsFunnelTrafficSources'>,
  query: FunnelTrafficSourcesQuery | null,
): FunnelTrafficSourcesState {
  const [result, setResult] = React.useState<FunnelTrafficSourcesState>({ state: 'idle' });
  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const key = query ? JSON.stringify(query) : null;
  React.useEffect(() => {
    if (!key || !query) {
      setResult({ state: 'idle' });
      return undefined;
    }
    const controller = new AbortController();
    setResult(current => (current.state === 'ready' ? { ...current, state: 'loading' } : { state: 'loading' }));
    client.getReportsFunnelTrafficSources({
      projectId: query.projectId,
      onboardingId: query.onboardingId,
      versionId: query.versionId,
      alias: query.alias,
      from: query.from,
      to: query.to,
      tz: query.tz,
      unit: query.unit,
      attributionModelId: query.attributionModelId,
      signal: controller.signal,
    })
      .then(response => {
        if (!mountedRef.current || controller.signal.aborted) return;
        setResult({ state: 'ready', data: response.data, asOf: response.asOf });
      })
      .catch((error: unknown) => {
        if (!mountedRef.current || controller.signal.aborted) return;
        setResult({ state: 'failed', error: error instanceof Error ? error.message : String(error) });
      });
    // The identity change (a new key) aborts the previous request — a response
    // for a scope the reader no longer looks at must never land.
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, client]);
  return result;
}
